<template>
  <DashboardLayout>
    <div class="p-4 sm:p-6 space-y-6 theme-transition">
      <!-- Page Header -->
      <div>
        <h1 class="text-3xl font-bold text-text-primary">Attendance</h1>
        <p class="text-text-secondary mt-1">Track your daily attendance</p>
      </div>

      <OvertimePanel />
      <p v-if="attendanceStore.activeRosterWindow && !attendanceStore.activeRosterWindow.flexible" class="text-sm text-text-primary">
        Shift ends: {{ new Date(attendanceStore.activeRosterWindow.effectiveEnd).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) }}
        <span v-if="attendanceStore.activeRosterWindow.otMinutes"> (including {{ attendanceStore.activeRosterWindow.otMinutes / 60 }} hours approved OT)</span>.
        Automatic clock-out: {{ new Date(attendanceStore.activeRosterWindow.effectiveEnd + 3600000).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) }}.
      </p>
      <p class="text-sm text-text-secondary">Complete location and face verification before closing. Wait for confirmation that attendance was saved; pending actions retry when connected.</p>
      <!-- Quick Action Cards -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Clock In/Out Card -->
        <div class="lg:col-span-2 bg-bg-secondary rounded-2xl p-6 sm:p-8 border border-border-primary relative overflow-hidden theme-transition">
          <!-- Animated Background -->
          <div class="absolute inset-0 bg-accent-primary/5"></div>

          <div class="relative z-10">
            <!-- Current Status -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
              <div>
                <p class="text-text-secondary text-sm font-medium">Current Status</p>
                <div class="flex items-center space-x-3 mt-2">
                  <div
                    :class="[
                      'w-3 h-3 rounded-full',
                      isClockedIn ? 'bg-success animate-pulse' : 'bg-error'
                    ]"
                  ></div>
                  <h2 class="text-2xl font-bold text-text-primary">
                    {{ isClockedIn ? 'Clocked In' : 'Clocked Out' }}
                  </h2>
                </div>
                <p v-if="activeSessionLabel" class="text-sm text-text-secondary mt-2">
                  {{ activeSessionLabel }}
                </p>
              </div>
              <div class="sm:text-right">
                <p class="text-text-secondary text-sm font-medium">Current Time</p>
                <p class="text-2xl font-bold text-text-primary mt-2">{{ currentTime }}</p>
              </div>
            </div>

            <!-- Clock In/Out Button -->
            <button
              @click="handleClockInOut"
              :disabled="isClockButtonDisabled"
              :class="[
                'w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-3',
                isClockedIn
                  ? 'bg-gradient-to-r from-error to-error/80 hover:from-error/90 hover:to-error text-white shadow-theme-lg shadow-error/20'
                  : 'bg-gradient-to-r from-accent-primary to-accent-secondary hover:from-accent-secondary hover:to-accent-primary text-white shadow-theme-lg shadow-accent-primary/20',
                isClockButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
              ]"
            >
              <svg v-if="loading || attendanceStore.clockActionInFlight" class="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>{{ clockButtonLabel }}</span>
            </button>

            <!-- Current Shift Duration -->
            <div v-if="currentShiftDuration" class="mt-6 p-4 bg-bg-primary/50 rounded-xl border border-border-primary">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-text-secondary text-sm">Current Shift Duration</p>
                  <p class="text-2xl font-bold text-text-primary mt-1">{{ currentShiftDuration }}</p>
                </div>
                <div class="w-12 h-12 bg-accent-primary/10 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Location & Requirements Card -->
        <div class="space-y-6">
          <!-- Location Status -->
          <div class="bg-bg-secondary rounded-2xl p-6 border border-border-primary theme-transition">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-2">
                <h3 class="text-lg font-bold text-text-primary">Location</h3>
                <button
                  @click="refreshLocation"
                  :disabled="refreshingLocation"
                  class="p-1.5 text-text-tertiary hover:text-accent-primary hover:bg-accent-primary/10 rounded-lg transition-all"
                  title="Refresh location"
                >
                  <svg
                    class="w-4 h-4"
                    :class="{ 'animate-spin': refreshingLocation }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
              <div
                :class="[
                  'w-3 h-3 rounded-full',
                  isLocationValid ? 'bg-success' : 'bg-error'
                ]"
              ></div>
            </div>
            <div class="flex items-center space-x-3">
              <div
                :class="[
                  'w-12 h-12 rounded-xl flex items-center justify-center',
                  isLocationValid ? 'bg-success/10' : 'bg-error/10'
                ]"
              >
                <svg
                  :class="[
                    'w-6 h-6',
                    isLocationValid ? 'text-success' : 'text-error'
                  ]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <div>
                <p
                  :class="[
                    'font-semibold',
                    isLocationValid ? 'text-success' : 'text-error'
                  ]"
                >
                  {{ isLocationValid ? 'Within Work Area' : 'Outside Work Area' }}
                </p>
                <p class="text-text-secondary text-sm mt-1">{{ locationDetails }}</p>
                <div v-if="!isLocationValid && locationDiagnostics" class="text-text-secondary text-xs mt-2 space-y-1">
                  <p v-if="locationDiagnostics.distance !== null">Phone-reported distance to saved pin: {{ locationDiagnostics.distance }}m</p>
                  <p>GPS accuracy: {{ locationDiagnostics.accuracy }} ? Reading age: {{ locationDiagnostics.age }}</p>
                  <details>
                    <summary class="cursor-pointer text-accent-primary">Location details</summary>
                    <p class="mt-1">Phone GPS: {{ locationDiagnostics.phone }}</p>
                    <p>Workplace pin: {{ locationDiagnostics.pin }}</p>
                    <p class="mt-1">If these differ while you are at work, refresh location and ask your manager to check the saved workplace pin.</p>
                  </details>
                </div>
              </div>
            </div>
          </div>

          <!-- Requirements -->
          <div class="bg-bg-secondary rounded-2xl p-6 border border-border-primary theme-transition">
            <h3 class="text-lg font-bold text-text-primary mb-4">Requirements</h3>
            <div class="space-y-3">
              <!-- PDPA Consent -->
              <div 
                class="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-bg-primary transition-colors cursor-pointer group"
                @click="$router.push('/pdpa-consent')"
              >
                <div class="flex items-center space-x-2">
                  <svg
                    :class="[
                      'w-5 h-5',
                      authStore.hasPDPAConsent ? 'text-success' : 'text-error'
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path v-if="authStore.hasPDPAConsent" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-text-primary text-sm group-hover:text-accent-primary transition-colors">PDPA Consent</span>
                </div>
                <div class="flex items-center space-x-1">
                  <span
                    :class="[
                      'text-sm font-medium',
                      authStore.hasPDPAConsent ? 'text-success' : 'text-error'
                    ]"
                  >
                    {{ authStore.hasPDPAConsent ? 'Given' : 'Required' }}
                  </span>
                  <svg v-if="!authStore.hasPDPAConsent" class="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              <!-- Face Registration -->
              <div 
                class="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-bg-primary transition-colors cursor-pointer group"
                @click="$router.push('/onboarding')"
              >
                <div class="flex items-center space-x-2">
                  <svg
                    :class="[
                      'w-5 h-5',
                      authStore.hasFaceRegistered ? 'text-success' : 'text-error'
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path v-if="authStore.hasFaceRegistered" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-text-primary text-sm group-hover:text-accent-primary transition-colors">Face Registration</span>
                </div>
                <div class="flex items-center space-x-1">
                  <span
                    :class="[
                      'text-sm font-medium',
                      authStore.hasFaceRegistered ? 'text-success' : 'text-error'
                    ]"
                  >
                    {{ authStore.hasFaceRegistered ? 'Complete' : 'Required' }}
                  </span>
                  <svg v-if="!authStore.hasFaceRegistered" class="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              <!-- Notifications -->
              <div 
                class="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-bg-primary transition-colors cursor-pointer group"
                @click="requestNotificationPermission"
              >
                <div class="flex items-center space-x-2">
                  <svg
                    :class="[
                      'w-5 h-5',
                      notificationPermission === 'granted' ? 'text-success' : 'text-error'
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path v-if="notificationPermission === 'granted'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-text-primary text-sm group-hover:text-accent-primary transition-colors">Notifications</span>
                </div>
                <div class="flex items-center space-x-1">
                  <span
                    :class="[
                      'text-sm font-medium',
                      notificationPermission === 'granted' ? 'text-success' : 'text-error'
                    ]"
                  >
                    {{ notificationPermission === 'granted' ? 'Enabled' : 'Disabled' }}
                  </span>
                  <svg v-if="notificationPermission !== 'granted'" class="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Today's Records -->
      <div class="bg-bg-secondary rounded-2xl p-6 border border-border-primary theme-transition">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-xl font-bold text-text-primary">Today's Records</h2>
            <p class="text-text-secondary text-sm mt-1">{{ format(new Date(), 'EEEE, MMMM d, yyyy') }}</p>
          </div>
          <button 
            @click="$router.push('/attendance-history')"
            class="px-4 py-2 bg-bg-primary hover:bg-bg-tertiary border border-border-primary rounded-xl text-text-primary transition-all text-sm font-medium"
          >
            View All
          </button>
        </div>

        <!-- Records List -->
        <div v-if="todaysRecords.length === 0" class="text-center py-12">
          <svg class="w-16 h-16 text-text-tertiary mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <p class="text-text-secondary">No records for today</p>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="record in todaysRecords"
            :key="record.id"
            class="bg-bg-primary rounded-xl p-4 border border-border-primary hover:border-accent-primary/50 transition-all duration-300 group cursor-pointer"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center space-x-3">
                <div
                  :class="[
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    record.type === 'clock-in' ? 'bg-success/10' : 'bg-error/10'
                  ]"
                >
                  <svg
                    :class="[
                      'w-5 h-5',
                      record.type === 'clock-in' ? 'text-success' : 'text-error'
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-text-primary font-semibold">{{ formatRecordType(record.type) }}</p>
                  <p class="text-text-secondary text-sm">{{ formatTime(record.timestamp) }}</p>
                </div>
              </div>
              <button
                v-if="record.videoUrl && record.videoUrl !== 'uploading...'"
                @click="openVideo(record.videoUrl)"
                class="p-2 bg-bg-secondary hover:bg-bg-tertiary rounded-lg transition-all"
              >
                <svg class="w-5 h-5 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </button>
            </div>
            <div v-if="record.location" class="mt-3 flex items-center space-x-2 text-text-tertiary text-xs">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              </svg>
              <span>{{ record.location.latitude.toFixed(4) }}, {{ record.location.longitude.toFixed(4) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Capture Modal -->
    <AttendanceVideoCapture
      v-if="showVideoCapture"
      @video-captured="onVideoCaptured"
      @cancel="cancelVideoCapture"
    />
  </DashboardLayout>
</template>

<script setup>
import OvertimePanel from '@/components/attendance/OvertimePanel.vue'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { format } from 'date-fns'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { useAttendanceStore } from '@/stores/attendance'
import { useCompanyStore } from '@/stores/company'
import { formatFirebaseError } from '@/utils/errorHandlers'
import { logStaffActivity } from '@/services/staffActivityLog'
import DashboardLayout from '@/components/layout/DashboardLayout.vue'
import AttendanceVideoCapture from '@/components/biometric/AttendanceVideoCapture.vue'
import { useNotifications } from '@/composables/useNotifications'

const toast = useToast()
const authStore = useAuthStore()
const attendanceStore = useAttendanceStore()
const companyStore = useCompanyStore()
const { notificationPermission, requestPermission } = useNotifications()

const loading = ref(false)
const refreshingLocation = ref(false)
const currentTime = ref('')
const showVideoCapture = ref(false)
const pendingAttendanceType = ref(null)
const hasCompanyContext = computed(() => !!authStore.userProfile?.companyId || authStore.isSuperAdmin)

let timeInterval = null

const isClockedIn = computed(() => attendanceStore.isClockedIn)

const activeSessionLabel = computed(() => {
  const record = attendanceStore.activeClockInRecord
  if (!record?.timestamp) return ''
  const date = record.timestamp instanceof Date ? record.timestamp : new Date(record.timestamp)
  if (Number.isNaN(date.getTime())) return ''
  const sameDay =
    date.getDate() === new Date().getDate() &&
    date.getMonth() === new Date().getMonth() &&
    date.getFullYear() === new Date().getFullYear()
  const timeLabel = sameDay
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  return `Since ${timeLabel} — tap Clock Out to end this session`
})
const todaysRecords = computed(() => attendanceStore.todaysRecords)
const currentGeofence = computed(() => attendanceStore.currentGeofence)

const isLocationValid = computed(() => {
  if (!attendanceStore.currentLocation) return false
  const userLat = attendanceStore.currentLocation.coords.latitude
  const userLng = attendanceStore.currentLocation.coords.longitude
  const accuracy = attendanceStore.currentLocation.coords.accuracy || 0

  const effectiveBranchId = attendanceStore.currentSchedule?.branchId || authStore.userProfile?.branchId
  const geolocationSettings = companyStore.getGeolocationSettings(effectiveBranchId)
  const companyGeoEnabled = companyStore.currentCompany?.settings?.geolocation?.enabled

  if (geolocationSettings?.enabled || companyGeoEnabled) {
    const result = companyStore.resolveAttendanceBranch(userLat, userLng, effectiveBranchId, accuracy)
    if (result.isValid) return true
  } else if (companyStore.isWithinLocationRadius(userLat, userLng, effectiveBranchId, accuracy)) {
    return true
  }

  return !!currentGeofence.value
})

const locationDetails = computed(() => {
  if (!attendanceStore.currentLocation) return 'Enable location services'

  const userLat = attendanceStore.currentLocation.coords.latitude
  const userLng = attendanceStore.currentLocation.coords.longitude
  const accuracy = attendanceStore.currentLocation.coords.accuracy || 0
  const effectiveBranchId = attendanceStore.currentSchedule?.branchId || authStore.userProfile?.branchId
  const geolocationSettings = companyStore.getGeolocationSettings(effectiveBranchId)
  const companyGeoEnabled = companyStore.currentCompany?.settings?.geolocation?.enabled

  if (geolocationSettings?.enabled || companyGeoEnabled) {
    const result = companyStore.resolveAttendanceBranch(userLat, userLng, effectiveBranchId, accuracy)
    if (result.isValid) {
      if (result.usedAlternateBranch) {
        return `At ${result.branchName} (roster lists a different branch)`
      }
      return `At ${result.branchName}`
    }

    const radius = result.radius || geolocationSettings?.radius || companyStore.currentCompany?.settings?.geolocation?.radius || 100
    if (!effectiveBranchId && !authStore.userProfile?.branchId) {
      return 'Please move within range of any authorized company branch'
    }
    return `You must be within ${radius}m of ${result.branchName}`
  }

  // If validation is disabled
  return 'Location Optional'
})

const locationDiagnostics = computed(() => {
  void currentTime.value
  const position = attendanceStore.currentLocation
  if (!position?.coords) return null
  const { latitude, longitude, accuracy } = position.coords
  const branchId = attendanceStore.currentSchedule?.branchId || authStore.userProfile?.branchId
  const branchSettings = companyStore.getGeolocationSettings(branchId)
  const settings = branchSettings?.enabled ? branchSettings : companyStore.currentCompany?.settings?.geolocation
  const validCoordinate = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
  const hasPin = validCoordinate(settings?.latitude) && validCoordinate(settings?.longitude)
  const hasPhone = validCoordinate(latitude) && validCoordinate(longitude)
  return {
    distance: hasPin && hasPhone ? Math.round(companyStore.calculateDistance(latitude, longitude, Number(settings.latitude), Number(settings.longitude))) : null,
    accuracy: Number.isFinite(accuracy) ? `?${Math.round(accuracy)}m` : 'unavailable',
    age: Number.isFinite(position.timestamp) ? `${Math.max(0, Math.round((Date.now() - position.timestamp) / 1000))}s` : 'unavailable',
    phone: hasPhone ? `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}` : 'unavailable',
    pin: hasPin ? `${Number(settings.latitude).toFixed(6)}, ${Number(settings.longitude).toFixed(6)}` : 'not configured',
  }
})

const canClockInOut = computed(() => {
  const needsFaceReg = attendanceStore.isFaceRecognitionGloballyEnabled && companyStore.currentCompany?.settings?.faceRecognitionRequired !== false
  return authStore.hasPDPAConsent && (!needsFaceReg || authStore.hasFaceRegistered) && isLocationValid.value
})

const clockCooldownSeconds = computed(() => {
  // Re-evaluate every second with the visible clock ticker
  void currentTime.value
  return Math.ceil(attendanceStore.getClockActionCooldownRemaining() / 1000)
})

const isClockButtonDisabled = computed(() => {
  return (
    loading.value ||
    attendanceStore.clockActionInFlight ||
    clockCooldownSeconds.value > 0 ||
    !canClockInOut.value
  )
})

const clockButtonLabel = computed(() => {
  if (loading.value || attendanceStore.clockActionInFlight) return 'Processing...'
  if (clockCooldownSeconds.value > 0) return `Please wait ${clockCooldownSeconds.value}s`
  return isClockedIn.value ? 'Clock Out' : 'Clock In'
})

const currentShiftDuration = computed(() => {
  const records = [...todaysRecords.value].sort((a, b) => {
    const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp)
    const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp)
    return timeA - timeB
  })

  // If currently clocked in, show duration of active shift
  if (attendanceStore.isClockedIn) {
    const latestClockIn = [...attendanceStore.attendanceRecords]
      .sort((a, b) => {
        const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp)
        const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp)
        return timeA - timeB
      })
      .reverse()
      .find(r => r.type === 'clock-in')

    if (latestClockIn) {
      const clockInTime = latestClockIn.timestamp?.toDate ? latestClockIn.timestamp.toDate() : new Date(latestClockIn.timestamp)
      const diff = new Date() - clockInTime
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      return `${hours}h ${minutes}m`
    }
    return '0h 0m'
  }

  // If clocked out, show duration of the most recent completed shift today
  const lastClockOut = records.reverse().find(r => r.type === 'clock-out')
  if (lastClockOut) {
    const clockOutTime = lastClockOut.timestamp?.toDate ? lastClockOut.timestamp.toDate() : new Date(lastClockOut.timestamp)
    // Find the clock-in that matches this clock-out
    const matchingClockIn = [...attendanceStore.attendanceRecords]
      .filter(r => {
        const t = r.timestamp?.toDate ? r.timestamp.toDate() : new Date(r.timestamp)
        return t < clockOutTime
      })
      .sort((a, b) => {
        const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp)
        const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp)
        return timeB - timeA // Descending to get the closest one
      })
      .find(r => r.type === 'clock-in')

    if (matchingClockIn) {
      const clockInTime = matchingClockIn.timestamp?.toDate ? matchingClockIn.timestamp.toDate() : new Date(matchingClockIn.timestamp)
      const diff = clockOutTime - clockInTime
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      return `${hours}h ${minutes}m`
    }
  }

  return null
})

const updateTime = () => {
  currentTime.value = format(new Date(), 'HH:mm:ss')
}

const formatRecordType = (type) => {
  switch (type) {
    case 'clock-in': return 'Clock In'
    case 'clock-out': return 'Clock Out'
    case 'break-start': return 'Break Start'
    case 'break-end': return 'Break End'
    default: return type
  }
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  try {
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    if (isNaN(d.getTime())) return 'Invalid Time'
    return format(d, 'HH:mm:ss')
  } catch (e) {
    return 'Invalid Time'
  }
}

const handleClockInOut = async () => {
  if (loading.value || attendanceStore.clockActionInFlight) return

  const actionType = isClockedIn.value ? 'clock-out' : 'clock-in'

  const cooldownMs = attendanceStore.getClockActionCooldownRemaining()
  if (cooldownMs > 0) {
    const reason = `Please wait ${Math.ceil(cooldownMs / 1000)}s before clocking again.`
    void logStaffActivity({
      authStore,
      action: actionType,
      outcome: 'failed',
      source: 'modern',
      reason,
    })
    toast.info(reason)
    return
  }

  const needsFaceReg = attendanceStore.isFaceRecognitionGloballyEnabled && companyStore.currentCompany?.settings?.faceRecognitionRequired !== false
  
  if (!canClockInOut.value) {
    const missingRequirements = []
    if (!authStore.hasPDPAConsent) missingRequirements.push('PDPA Consent')
    if (needsFaceReg && !authStore.hasFaceRegistered) missingRequirements.push('Face Registration')
    if (!isLocationValid.value) missingRequirements.push('Valid Location')
    const reason = `Missing requirements: ${missingRequirements.join(', ')}`
    void logStaffActivity({
      authStore,
      action: actionType,
      outcome: 'failed',
      source: 'modern',
      reason,
    })
    toast.error(reason)
    return
  }

  void logStaffActivity({
    authStore,
    action: actionType,
    outcome: 'attempted',
    source: 'modern',
  })

  // If face recognition is disabled globally OR by company, clock in directly
  if (!needsFaceReg) {
    loading.value = true
    try {
      toast.info('Processing attendance...')
      await attendanceStore.clockInOut(actionType, 100, null, { source: 'modern' })
      toast.success(`Successfully ${actionType === 'clock-in' ? 'clocked in' : 'clocked out'}!`)
    } catch (error) {
      console.error('Clock in/out error:', error)
      toast.error(formatFirebaseError(error))
    } finally {
      loading.value = false
    }
    return
  }

  pendingAttendanceType.value = actionType
  loading.value = true
  showVideoCapture.value = true
}

const onVideoCaptured = async (videoBlob, confidence) => {
  showVideoCapture.value = false
  loading.value = true

  try {
    if (attendanceStore.clockActionInFlight) {
      toast.info('Attendance action already in progress. Please wait.')
      return
    }

    const type = pendingAttendanceType.value || (isClockedIn.value ? 'clock-out' : 'clock-in')
    pendingAttendanceType.value = null
    toast.info('Processing attendance...')

    // Convert confidence to 0-100 scale if it's probabilistic (0-1)
    const confidenceScore = confidence <= 1 ? confidence * 100 : confidence
    await attendanceStore.clockInOut(type, confidenceScore, videoBlob, { source: 'modern' })

    toast.success(`Successfully ${type === 'clock-in' ? 'clocked in' : 'clocked out'}!`)
  } catch (error) {
    console.error('Clock in/out error:', error)
    toast.error(formatFirebaseError(error))
  } finally {
    loading.value = false
  }
}

const cancelVideoCapture = () => {
  const action = pendingAttendanceType.value || (isClockedIn.value ? 'clock-out' : 'clock-in')
  void logStaffActivity({
    authStore,
    action,
    outcome: 'cancelled',
    source: 'modern',
    reason: 'Video capture cancelled',
  })
  showVideoCapture.value = false
  pendingAttendanceType.value = null
  loading.value = false
}

const openVideo = (url) => {
  window.open(url, '_blank')
}

const refreshLocation = async () => {
  if (refreshingLocation.value) return
  refreshingLocation.value = true
  try {
    await attendanceStore.getCurrentLocation()
    toast.success('Location updated')
  } catch (error) {
    console.error('Error refreshing location:', error)
    toast.error(error?.message || 'Failed to get latest location')
  } finally {
    refreshingLocation.value = false
  }
}

const requestNotificationPermission = async () => {
  try {
    const success = await requestPermission()
    if (success) {
      toast.success('Notifications enabled!')
    }
  } catch (error) {
    console.warn('Notification permission error:', error)
  }
}

onMounted(async () => {
  updateTime()
  timeInterval = setInterval(updateTime, 1000)

  try {
    if (hasCompanyContext.value) {
      await companyStore.loadCompanies()
      await companyStore.loadBranches()
      await attendanceStore.loadGeofences()
    }
    await Promise.all([
      attendanceStore.loadTodaysAttendance(),
      attendanceStore.loadSystemConfigs(),
      attendanceStore.loadCurrentSchedule()
    ])

    try {
      // Check for geolocation permission before calling automatically to avoid violation
      if ('permissions' in navigator) {
          const geoStatus = await navigator.permissions.query({ name: 'geolocation' })
          if (geoStatus.state === 'granted') {
              await attendanceStore.getCurrentLocation()
          } else {
              console.log('Geolocation permission state:', geoStatus.state)
          }
      } else {
          // Fallback if permissions API not supported
          await attendanceStore.getCurrentLocation()
      }
    } catch (geoError) {
      console.warn('Geolocation failed:', geoError)
      // Do not block initialization or show generic error
      // UI will show "Enable location services" or "Required" based on state
      if (geoError.code === 1) {
          toast.info('Please enable location access to clock in.')
      }
    }
  } catch (error) {
    console.error('Error loading data:', error)
    toast.error(formatFirebaseError(error))
  }
})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
})
</script>
